
import Vue from 'vue'
import { mapGetters } from 'vuex'
import socialNetworks from './components/socialNetworks/index.vue'
import safety from './components/safety/index.vue'
import kyc from './components/kyc/index.vue'
import kycStatusCard from './components/kyc-status-card/index.vue'
import accessRepairStatus from '@/components/pages/whitelistID/components/account/components/access-repair/accessRepairStatus/index.vue'
import PassportNotification from '@/components/pages/whitelistID/components/account/components/notifications/index.vue'
import { AccessStatus, IUnifiedAcc, KycStatus, NicknameErrors } from './components/types'
import { systemPromoCodes } from '@/constants/baseConstatnts'
import { IKycLinkDataResponse, VerificationStatuses } from './types'
import { copyToClipboard } from '@whitelist/wl-old-library/utils/other'
import services from '@/services'
import { debounce } from 'lodash'
import CustomSelect from '@whitelist/wl-old-library/components/global/customSelect/index.vue'

const debouncedCheckNickname = debounce((func) => {
  func()
}, 500)

export default Vue.extend({
  name: 'myAccountPage',
  data () {
    return {
      kycDataLoaded: false,
      kycData: {},
      unifiedAcc: [] as IUnifiedAcc[],
      currentKycLevelData: {},
      kycOpen: false,
      accessRepairOpen: false,
      currentYPosition: 0,
      scrollTo: { x: 0, y: 0 },
      accessRepairData: {
        confirmationMethod: '',
        status: '',
        reason: '',
        loginMethod: '',
        email: '',
        currentRecoveryTypes: []
      },
      uuidCopiedTimer: 0,
      editNickname: '',
      editNicknameMode: false,
      checkNicknameLoading: false,
      nicknameError: null as null | NicknameErrors,
      multiprofileSocket: null,
      editingProfilename: '',
      editProfileNameMode: false,
      profileNameUpdateLoading: false,
      switchProfileLoading: false,
      profileNameRegexp: /^[^\s]{5,20}$/,
      multiprofileFaqLink: null
    }
  },
  watch: {
    'getUserData.currentAccessRepairMethods' (value: any) {
      if (value.length > 1) this.kycOpen = true
    },
    'getUserData.nickname': {
      handler (v) {
        this.editNickname = v
      },
      deep: true
    },
    getIsLoaded: {
      handler (isLoaded) {
        if (isLoaded) this.checkNewNickname()
      }
    },
    editNickname () {
      if (this.editNickname === this.getUserData.nickname || this.editNickname === '') return
      this.checkNicknameLoading = true
      debouncedCheckNickname(this.checkNickname)
    }
  },
  computed: {
    ...mapGetters('user', ['getUserData', 'getIsLoaded']),
    ...mapGetters('notifications', ['getPassport']),
    selectedProfileData () {
      const result = this.getUserData.profiles.find((profile: any) => profile.status === 'active')
      return result
    },
    selectedProfileListName () {
      return this.selectedProfileData?.name
    },
    profileList () {
      return this.getUserData.profiles.map((profile: any) => {
        return {
          key: profile.id,
          value: profile.name,
          status: profile.status
        }
      }).filter((profile: any) => profile.status !== 'init')
    },
    isMobile (): boolean {
      return 'ontouchstart' in window
    },
    editNicknameDescription () {
      if (this.checkNicknameLoading) return 'Проверяем никнейм...'
      switch (this.nicknameError) {
        case NicknameErrors.FORBIDDEN_WORD:
          return 'Никнейм не должен содержать неуважительные слова, официальные наименования Whitelist (например, названия продуктов).'
        case NicknameErrors.INVALID_UPPER_CASE:
          return 'В никнейме не может быть заглавных букв. Все буквы вводятся в инпут только строчными'
        case NicknameErrors.UNIQUENESS_ERROR:
          return 'Этот никнейм уже используется. Попробуйте добавить цифры, буквы, нижнее подчёркивание или точки.'
        case NicknameErrors.INVALID_LENGTH:
          return 'Никнейм должен содержать от 5 до 20 символов.'
        case NicknameErrors.INVALID_SYMBOLS:
          return 'Используйте латинские буквы, цифры, нижнее подчёркивание и точки.'
      }
      if (this.nicknameEditIconDisabled) return 'Используйте латинские буквы, цифры, нижнее подчёркивание и точки.'
      return 'Этот никнейм свободен!'
    },
    tooltipPromocode () {
      const { promo } = this.getUserData
      return systemPromoCodes.includes(promo) ? this.$t('registration.refSystemCodeExplain') : this.$t('registration.refCodeExplain', { name: promo })
    },
    kycType (): KycStatus | string {
      let result = ''
      const extendedStatus = this.kycData?.extended?.status
      const advancedStatus = this.kycData?.advanced?.status
      switch (extendedStatus) {
        case VerificationStatuses.wait:
          result = KycStatus.loading
          break
        case VerificationStatuses.declined:
          result = KycStatus.failed
          break
        case VerificationStatuses.double:
          result = KycStatus.failedDouble
          break
      }
      if (advancedStatus === VerificationStatuses.addressConflict) {
        result = KycStatus.conflict
      }
      return result
    },
    nicknameEditIconDisabled () {
      return this.nicknameError || this.editNickname === this.getUserData.nickname || this.editNickname === ''
    },
    profileEditProfilenameIconDisabled () {
      return !this.isNewProfilenameValid || this.editingProfilename === this.selectedProfileListName || this.editingProfilename === ''
    },
    isNewProfilenameValid () {
      return this.editingProfilename.match(this.profileNameRegexp)
    }
  },
  components: {
    socialNetworks,
    safety,
    kyc,
    accessRepairStatus,
    PassportNotification,
    kycStatusCard,
    CustomSelect
  },
  mounted () {
    this.listenProfileEvents()
    const urlParams = new URLSearchParams(window.location.search)
    const verificationShow = urlParams.get('verification-show') === 'true'
    const autoProceedKyc = urlParams.get('auto-proceed-kyc') === 'true'
    if (autoProceedKyc) this.proceedVerification('auto')
    if (verificationShow) {
      this.kycOpen = true
    }
    const editNicknameModeParam = urlParams.get('edit-nickname') === 'true'
    if (editNicknameModeParam) this.editNicknameMode = true
    const kyc = urlParams.get('kyc') === 'true'
    if (kyc) this.goToAnotherAcc()
  },
  methods: {
    listenProfileEvents () {
      this.multiprofileSocket = new WebSocket(`wss://${document.location.hostname}/ws/active-user`)
      this.multiprofileSocket.onmessage = (message: any) => {
        let response: any
        try {
          response = JSON.parse(message.data)
        } catch (e) {
          // Не-JSON кадр (ping/keepalive) не должен ломать обработчик и глушить синхронизацию.
          return
        }
        if (response.action === 'activeUserChanged') {
          const userId = response.payload.userId
          this.$store.commit('user/changeActiveUser', { userId })
          // Быстрый путь лестницы: сначала приводим токен к активному профилю, потом
          // перезагружаемся. Без refresh перезагрузка упёрлась бы в 401
          // (code=active_profile_changed) и вызвала вторую перезагрузку из интерцептора.
          this.$services.authorization.user.refreshToken()
            .finally(() => { window.location.reload() })
        }
        if (response.action === 'userNameChanged') {
          const userId = response.payload.userId
          const name = response.payload.name
          this.editProfileNameMode = false
          this.$store.commit('user/updateProfileList', { userId, name })
        }
      }
    },
    editProfileClickHandler () {
      this.editProfileNameMode = true
      this.editingProfilename = this.selectedProfileListName
    },
    async loadFaqLink () {
      const response = await this.$services.authorization.user.getDocsForSlug({ slug: 'multiprofile' })
      if (response.data?.multiprofile?.link) this.multiprofileFaqLink = response.data.multiprofile.link
    },
    moveToFaq () {
      if (this.multiprofileFaqLink) {
        window.open(this.multiprofileFaqLink, '_blank')
      }
    },
    clickPrivacyLinkHandler () {
      const routeData = this.$router.resolve({ name: 'whitelistIDLegalInformation' })
      window.open(routeData.href, '_blank')
    },
    closeFromStatusCard () {
      this.getKycData()
    },
    async getKycData () {
      const kycDataResponse: { success: boolean, data: IKycLinkDataResponse, status: number } = await this.$services.authorization.user.kycData()
      if (!kycDataResponse.success) return
      this.kycData = kycDataResponse.data
      if (this.kycData.advanced.status === VerificationStatuses.addressConflict) {
        const response = await this.$services.authorization.user.getAccountConflict()
        this.$store.dispatch('alertsBus/openAlert', {
          title: this.$t('alerts.titles.attention'),
          type: 'warning',
          autoDelete: true,
          content: this.$t('alerts.content.extendedNotFinished')
        })
        if (response.success) {
          this.unifiedAcc = response.data
        }
      }
      // Mock
      // const kycDataResponse: { success: boolean, data: IKycLinkDataResponse, status: number } = await this.$services.authorization.user.kycData()
      // if (!kycDataResponse.success) return
      // this.kycData = {
      //   basic: {
      //     status: 'accepted',
      //     reason: null
      //   },
      //   extended: {
      //     status: 'notStarted',
      //     reason: null
      //   },
      //   advanced: {
      //     status: 'wait',
      //     reason: null
      //   }
      // }
      // // const kycLinkResponse: { data: { url: string, errors: string[] }, success: boolean } = await this.$services.authorization.user.kycLink()
      // const kycLinkResponse = { success: true, data: { url: 'test', errors: ['mlsdf'] } }
      // if (kycLinkResponse.success) this.kycData.link = kycLinkResponse.data.url
      // else {
      //   this.$store.dispatch('alertsBus/openAlert', {
      //     title: this.$t('alerts.titles.error'),
      //     type: 'error',
      //     autoDelete: true,
      //     content: kycLinkResponse.data.errors.join('\n')
      //   })
      // }
      // if (this.kycData.advanced.status === VerificationStatuses.addressConflict) {
      //   const response = await this.$services.authorization.user.getAccountConflict()
      //   this.$store.dispatch('alertsBus/openAlert', {
      //     title: this.$t('alerts.titles.attention'),
      //     type: 'warning',
      //     autoDelete: true,
      //     content: this.$t('alerts.content.extendedNotFinished')
      //   })
      //   if (response.success) {
      //     this.unifiedAcc = response.data
      //   }
      // }
    },
    proceedVerification (initialize = 'proceed') {
      const self = this as any
      if (this.getUserData.verificationRights.levelName === 'basic' || initialize === 'auto') {
        self.$store.dispatch('popupModule/openPopup', {
          type: 'confirmPopup',
          letDefaultClose: true,
          title: self.$t('popups.titles.transferUserData'),
          content: self.$t('popups.content.doYouHaveAcc'),
          actions: [
            {
              title: self.$t('popups.actions.transfer'),
              isAccent: false,
              callback () {
                self.goToAnotherAcc()
              }
            },
            {
              title: self.$t('popups.actions.kycProceed'),
              isAccent: false,
              callback () {
                self.$store.commit('popupModule/setClosed')
                self.agreeDataIsProof()
              }
            }
          ]
        })
        const url = new URL(window.location.href)
        url.searchParams.delete('auto-proceed-kyc')
        window.history.replaceState(null, '', url.toString())
      } else {
        self.agreeDataIsProof()
      }

      // Метки регистрирующие прохождение kyc
      window._tmr.push({ type: 'reachGoal', id: 3467065, goal: 'KycVerificationRequest' })
      window.ym(95601145, 'reachGoal', 'KycVerificationRequest', { userId: this.getUserData.uuid })
    },
    goToAnotherAcc () {
      const self = this as any
      self.$store.dispatch('popupModule/openPopup', {
        type: 'action',
        letDefaultClose: true,
        title: self.$t('popups.titles.goToAnotherAcc'),
        content: '',
        slot: {
          name: 'goToAnotherAcc'
        }
      })
    },
    agreeDataIsProof () {
      const self = this as any
      self.$store.dispatch('popupModule/openPopup', {
        type: 'action',
        letDefaultClose: true,
        title: self.$t('popups.titles.agreementDataProof'),
        content: '',
        slot: {
          name: 'agreeDataIsProof'
        }
      })
    },
    scrollToCurrentRepair () {
      this.scrollTo.y += 0.1
      this.accessRepairOpen = true
    },
    onChangeEditNickname (val: string) {
      this.editNickname = val?.trim()?.toLowerCase()
    },
    onChangeEditingProfilename (val: string) {
      this.editingProfilename = val?.trim()
    },
    changeKycAccordionStatus (value: boolean) {
      const vm = this as any
      vm.scrollTo.y += 0.1
      const scrollToElement: HTMLElement | null = document.getElementById('kyc-extended')
      const nextScrollPosition = Math.abs(scrollToElement?.getBoundingClientRect().top ?? 0)
      vm.kycOpen = value
      if (!value) return
      vm.scrollTo.y = nextScrollPosition
    },
    async getKycLevels () {
      const kycLevelsResponse = await this.$services.authorization.user.kycLevels()
      if (!kycLevelsResponse.success) return
      this.currentKycLevelData = kycLevelsResponse.data.find((level: any) => level.levelName === this.getUserData.verificationRights.levelName)
    },
    async getAccessInfo (created?: boolean) {
      await this.$store.dispatch('user/loadUser')
      this.recoveryId = this.getUserData?.verificationRights?.currentRecovery || this.$route.query.recoveryId
      if (this.$route.query.recoveryId) this.$router.replace({ recoveryId: null })
      if (!this.recoveryId) return
      const getAccessInfo = await this.$services.authorization.user.getAccessInfo({ recoveryId: this.recoveryId })
      const isActive = getAccessInfo.data.status === AccessStatus.work || getAccessInfo.data.status === AccessStatus.repeat
      if (isActive) this.$store.commit('user/setCurrentAccessRepairMethods', getAccessInfo.data.currentRecoveryTypes)
      this.accessRepairData = getAccessInfo.data
      if (created && isActive) this.$emit('openAccessRepair')
    },
    handleUuidCopy (): void {
      copyToClipboard(this.getUserData.uuid).then(() => {
        window.clearTimeout(this.uuidCopiedTimer)
        this.uuidCopiedTimer = window.setTimeout(() => {
          this.uuidCopiedTimer = 0
        }, 1500)
      })
    },
    cancelEditProfilename (): void {
      this.editProfileNameMode = false
      this.editingProfilename = ''
    },
    cancelEditNickname (): void {
      this.editNicknameMode = false
      this.editNickname = this.getUserData.nickname
      this.nicknameError = null
    },
    checkNewNickname (): void {
      const traderWord = this.getUserData.nickname.slice(0, 6)
      const nicknameID = this.getUserData.nickname.slice(6)
      const editedNickname = localStorage.getItem('editedNickname') === 'true'
      if ((traderWord && Number(nicknameID)) && !editedNickname) {
        this.$store.dispatch('alertsBus/openAlert', {
          title: 'Добавили никнейм',
          type: 'info',
          autoDelete: false,
          content: 'Теперь вместо вашего ФИО отображается никнейм. Он станет публичным и будет показываться в топе лидеров, если вы решите в нём участвовать.',
          action: {
            text: 'Изменить никнейм',
            callback: () => {
              this.editNicknameMode = true
            },
            position: 'bottom'
          }
        })
      }
      localStorage.setItem('editedNickname', 'true')
    },
    async checkNickname () {
      this.checkNicknameLoading = true
      this.nicknameError = null
      // @ts-ignore
      const response = await services.authorization.user.checkNickname({
        nickname: this.editNickname
      })
      // @ts-ignore
      if (!response.success) {
        // @ts-ignore
        this.nicknameError = response.data.errors[0]
      }
      this.checkNicknameLoading = false
    },
    async acceptNickname () {
      if (this.checkNicknameLoading || this.nicknameEditIconDisabled) return
      this.checkNicknameLoading = true
      this.nicknameError = null
      // @ts-ignore
      const response = await services.authorization.user.editNickname({
        nickname: this.editNickname
      })
      // @ts-ignore
      if (response.success) {
        this.$store.commit('user/setUserNickname', this.editNickname)
        this.editNicknameMode = false
        this.$store.dispatch('alertsBus/openAlert', {
          title: 'Никнейм изменён',
          type: 'success',
          autoDelete: true,
          content: `Теперь ваше публичное имя — ${this.editNickname}. Его смогут увидеть другие пользователи Личного кабинета в виджете лидерборда.`
        })
      } else {
        // @ts-ignore
        if (response.data?.errors?.length) {
          // @ts-ignore
          this.nicknameError = response.data.errors[0]
        }
        this.$store.dispatch('alertsBus/openAlert', {
          title: 'Никнейм не изменён',
          type: 'error',
          autoDelete: true,
          content: 'Не удалось изменить текущий никнейм. Попробуйте снова.'
        })
      }
      this.checkNicknameLoading = false
    },
    async onProfileSwitch (el: any) {
      // Клик по уже активному профилю: CustomSelect эмитит input и для него — лишний switch
      // на самого себя не отправляем.
      if (this.selectedProfileData && String(el.key) === String(this.selectedProfileData.id)) return

      this.switchProfileLoading = true
      const showSwitchError = () => {
        this.$store.dispatch('alertsBus/openAlert', {
          title: 'Не удалось переключить профиль',
          type: 'error',
          autoDelete: true,
          content: 'Попробуйте еще раз. Если проблема повторяется - обратитесь в поддержку.'
        })
      }
      try {
        // ⚠️ request() никогда не бросает — он возвращает {success, status, data}. Поэтому
        // прежний catch был недостижим: при 400/500 пользователь не видел ошибки, зато страница
        // всё равно перезагружалась. Проверяем success явно.
        const response = await this.$services.authorization.user.switchUserProfilename({ profileId: el.key })
        if (!response || response.success === false) {
          showSwitchError()
          this.switchProfileLoading = false
          return
        }
        // Токены под новый профиль паспорт уже поставил в куки ответом switch (WL-1351),
        // отдельный refresh не нужен — перезагружаемся сразу в новый контекст.
        window.location.reload()
      } catch (e) {
        showSwitchError()
        this.switchProfileLoading = false
      }
    },
    async acceptProfilenameUpdate () {
      if (this.profileEditProfilenameIconDisabled) return
      this.profileNameUpdateLoading = true
      try {
        await this.$services.authorization.user.updateUserProfilename({
          profileId: this.selectedProfileData.id,
          name: this.editingProfilename
        })
      } catch (e) {
        this.$store.dispatch('alertsBus/openAlert', {
          title: 'Профиль не переименован',
          type: 'error',
          autoDelete: true,
          content: `Не удалось переименовать профиль ${this.editingProfilename}. Попробуйте еще раз.`
        })
      } finally {
        this.profileNameUpdateLoading = false
      }
    }
  },
  async created () {
    this.loadFaqLink()
    await Promise.all([this.getAccessInfo(true), this.getKycLevels(), this.getKycData()])
    this.kycDataLoaded = true
    if (this.getUserData.verificationRights.levelName !== 'advanced') this.kycOpen = true
  }
})
